import { useClientHandle } from '@urql/vue';
import { debounce } from 'throttle-debounce';
import { type MaybeRefOrGetter, onMounted, onUnmounted, ref, toValue, watch } from 'vue';

import type {
  GetSeefelderDeliveryQuery,
  GetSeefelderDeliveryQueryVariables,
} from '@/generated/graphql';
import GET_SEEFELDER_DELIVERY from '@/graphql/getSeefelderDelivery.gql';

import { useFischer } from './fischerPlugin';

export type SeefelderDelivery = NonNullable<GetSeefelderDeliveryQuery['getSeefelderDelivery']>;

const SHIPPING_GROUP_REPLENISHMENT = 'replenishment';
const QUANTITY_DEBOUNCE_MS = 300;

/**
 * A replenishment delivery of this many DISPLAYED working days or more is shown
 * as "delivery time on request" rather than as a day count. The displayed value
 * already includes the +1 replenishment day the gateway applies, so 26 here
 * means 25 working days as reported by the API.
 */
export const ON_REQUEST_FROM_WORKING_DAYS = 26;

export const isSeefelderDeliveryOnRequest = (delivery: SeefelderDelivery | null): boolean => {
  if (delivery?.shippingGroup !== SHIPPING_GROUP_REPLENISHMENT) {
    return false;
  }
  const days = delivery.deliveryTimeInWorkingDays;
  return typeof days === 'number' && days >= ON_REQUEST_FROM_WORKING_DAYS;
};

/**
 * In-flight and settled requests, shared process-wide so that a list of many
 * Seefelder products at the same quantity issues a single request.
 */
const deliveryCache = new Map<string, Promise<SeefelderDelivery | null>>();

/** Test seam — resets the shared cache between cases. */
export const clearSeefelderDeliveryCache = () => deliveryCache.clear();

const normaliseQuantity = (quantity: number | null | undefined): number => {
  const parsed = Math.floor(Number(quantity));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

/**
 * Fetches the live Seefelder delivery time for a product, client-side only.
 *
 * `delivery` stays null until an answer arrives, and stays null on any
 * failure so the caller can fall back to the static dictionary label.
 * `hasSettled` distinguishes "haven't heard back yet" (render nothing) from
 * "heard back with nothing usable" (render the static fallback): it starts
 * false and flips to true once, the first time a fetch attempt completes for
 * any reason — success, error, rejection, or a null/unknown-sku result — and
 * never flips back, including across later debounced refetches.
 */
export const useSeefelderDelivery = (
  articleNumber: MaybeRefOrGetter<string | null | undefined>,
  quantity: MaybeRefOrGetter<number | null | undefined>,
  enabled: MaybeRefOrGetter<boolean>,
) => {
  const fischer = useFischer();
  const handle = useClientHandle();
  const delivery = ref<SeefelderDelivery | null>(null);
  const hasSettled = ref(false);

  let isMounted = false;
  let isActive = true;

  // Bumped on every fetchDelivery() call. A response is only written to
  // `delivery` if its call is still the most recent one issued by this
  // composable instance — otherwise a slow request for a since-abandoned
  // sku/quantity could resolve after a faster, newer one and clobber it
  // with stale data (last-write-wins violation).
  let requestGeneration = 0;

  const fetchDelivery = async () => {
    if (IS_SSR || !isMounted || !toValue(enabled)) {
      return;
    }

    const currentGeneration = ++requestGeneration;

    const articleNumberValue = toValue(articleNumber);
    const requestedQuantity = normaliseQuantity(toValue(quantity));

    if (!articleNumberValue) {
      // No article number to look up (and no mock to fall back on anymore).
      // Still mark the attempt as settled: `hasSettled === false` means
      // "render nothing", and a product we can never look up must show the
      // static fallback label instead of a permanently blank row.
      if (isActive) {
        hasSettled.value = true;
      }
      if (isActive && currentGeneration === requestGeneration) {
        delivery.value = null;
      }
      return;
    }

    const cacheKey = `${articleNumberValue}|${requestedQuantity}`;

    let request = deliveryCache.get(cacheKey);
    if (!request) {
      request = (async () => {
        const { data, error } = await handle.client
          .query<GetSeefelderDeliveryQuery, GetSeefelderDeliveryQueryVariables>(
            GET_SEEFELDER_DELIVERY,
            {
              articleNumber: articleNumberValue,
              quantity: requestedQuantity,
              baseSiteId: fischer.baseSiteId(),
              apiLang: fischer.catalogLocale(),
            },
            { requestPolicy: 'cache-first' },
          )
          .toPromise();
        if (error) {
          throw error;
        }
        return data?.getSeefelderDelivery ?? null;
      })();

      // A failed request must not be remembered, or the fallback label would
      // stick for the rest of the session.
      request.catch(() => deliveryCache.delete(cacheKey));
      deliveryCache.set(cacheKey, request);
    }

    try {
      const result = await request;
      // An attempt completed — even a stale one — so the caller can stop
      // waiting and fall back if the (still current) result is unusable.
      if (isActive) {
        hasSettled.value = true;
      }
      if (isActive && currentGeneration === requestGeneration) {
        delivery.value = result;
      }
    } catch {
      if (isActive) {
        hasSettled.value = true;
      }
      if (isActive && currentGeneration === requestGeneration) {
        delivery.value = null;
      }
    }
  };

  const debouncedFetch = debounce(QUANTITY_DEBOUNCE_MS, () => {
    void fetchDelivery();
  });

  onMounted(() => {
    isMounted = true;
    void fetchDelivery();
  });

  onUnmounted(() => {
    isActive = false;
    debouncedFetch.cancel?.();
  });

  watch(
    () => [toValue(articleNumber), normaliseQuantity(toValue(quantity)), toValue(enabled)],
    () => {
      // Nothing has ever settled yet (e.g. the mount-time fetch bailed
      // because `enabled` was still false) - fetch right away instead of
      // waiting out the debounce, or an already-rendered label blanks for
      // no reason while we wait. Once something is on screen, further
      // changes should still debounce as normal.
      if (!hasSettled.value) {
        void fetchDelivery();
        return;
      }
      debouncedFetch();
    },
  );

  return { delivery, hasSettled };
};
